import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Link,
  Paper,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { styled } from '@mui/material/styles';

const InformationCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.paper,
  backdropFilter: `blur(${theme.custom?.glassBlur || 16}px)`,
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: `${theme.custom?.cardRounding || 28}px`,
  padding: theme.spacing(3),
  color: '#ffffff',
  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.5)',
  overflowX: 'hidden',
}));

const FieldValue = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: '1rem',
  color: '#e8f9ff',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.75rem',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'rgba(255, 255, 255, 0.6)',
  marginBottom: '4px',
}));

const ImmichAbout = ({ apiBaseUrl, token }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!apiBaseUrl || !token) {
      setLoading(false);
      setError('Not authenticated.');
      return;
    }
    const controller = new AbortController();

    const fetchAbout = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiBaseUrl}/api/immich/about`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
          timeout: 15000
        });
        setInfo(response.data);
        setError(null);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error('ImmichAbout: Unable to load about data', err);
        const detail = err.response?.data?.detail;
        setError(detail ? (typeof detail === 'string' ? detail : JSON.stringify(detail)) : 'Unable to load Immich server metadata.');
      } finally {
        setLoading(false);
      }
    };

    fetchAbout();
    return () => controller.abort();
  }, [apiBaseUrl, token]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, borderRadius: '12px', border: '1px solid rgba(255, 68, 68, 0.4)' }}>
        {error}
      </Alert>
    );
  }

  if (!info) {
    return null;
  }

  const summaryFields = [
    { label: 'Version', value: info.version, url: info.versionUrl },
    { label: 'Build', value: info.build, url: info.buildUrl },
    { label: 'Build Image', value: info.buildImage, url: info.buildImageUrl },
    { label: 'Repository', value: info.repository, url: info.repositoryUrl },
    { label: 'Source Ref', value: info.sourceRef, url: info.sourceUrl },
    { label: 'Source Commit', value: info.sourceCommit, url: info.sourceUrl }
  ];

  const toolingFields = [
    { label: 'Node.js', value: info.nodejs },
    { label: 'ExifTool', value: info.exiftool },
    { label: 'FFmpeg', value: info.ffmpeg },
    { label: 'libvips', value: info.libvips },
    { label: 'ImageMagick', value: info.imagemagick }
  ];

  const renderField = ({ label, value, url }) => (
    <Grid item xs={12} sm={6} md={4} key={label}>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start', gap: 0.5 }}>
        <Box component="span" sx={{ wordBreak: 'break-all' }}>{value || '—'}</Box>
        {value && url && (
          <Link
            href={url}
            target="_blank"
            rel="noreferrer"
            sx={{ fontSize: '0.85rem', color: '#00ff88', ml: 0 }}
          >
            View
          </Link>
        )}
      </FieldValue>
    </Grid>
  );

  return (
    <InformationCard elevation={0}>
      <Stack spacing={1} mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Immich Server Details
        </Typography>
        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
          Licensed: {info.licensed ? 'Yes' : 'No'} · Build Image: {info.buildImage}
        </Typography>
      </Stack>

      <Grid container spacing={2} mb={3}>
        {summaryFields.map(field => renderField(field))}
      </Grid>

      <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '0.2em', mb: 1 }}>
        Toolchain
      </Typography>
      <Grid container spacing={2}>
        {toolingFields.map(({ label, value }) => (
          <Grid item xs={12} sm={6} md={4} key={label}>
            <FieldLabel>{label}</FieldLabel>
            <FieldValue>{value || '—'}</FieldValue>
          </Grid>
        ))}
      </Grid>
    </InformationCard>
  );
};

export default ImmichAbout;
